<template>
  <div>
    <b-row
      class="gutters-0 align-items-end new-gutters-sm"
      :key="periodKey"
    >
      <b-col
        cols="4"
      >
        <campaign-period
          :periods="periods"
          :period-index="periodIndex"
          :period-date-range="periodDateRange"
          :is-period-running="isPeriodRunning"
          :has-period-ended="hasPeriodEnded"
          @updateDates="updateDates"
        />
      </b-col>
      <b-col
        cols="3"
      >
        <period-budget
          :period-index="periodIndex"
          :initial-budget="budgetValue"
          :has-period-ended="hasPeriodEnded"
          @updateBudget="updateBudget"
        />
      </b-col>
      <b-col
        v-if="isAddImpressionTargetChecked"
        cols="3"
      >
        <impression-target
          :period-index="periodIndex"
          :initial-impression-target="impressionTargetValue"
          :has-period-ended="hasPeriodEnded"
          :is-add-impression-target-checked="isAddImpressionTargetChecked"
          @updateImpressionTarget="updateImpressionTarget"
        />
      </b-col>
      <b-col
        lg="2"
      >
        <button
          v-if="periods.length > 1"
          class="btn btn-danger"
          @click="deletePeriod"
          :disabled="isDeletePeriodDisabled"
        >
          <i class="fa-light fa-trash-can" />
        </button>
      </b-col>
    </b-row>
    <b-row
      v-if="!GET_IS_CAMPAIGN_ENDED"
      class="gutters-0"
    >
      <p
        v-if="shouldDisplayValidationMessages"
        class="mb-0 alert alert-sm alert-danger mt-3"
      >
        <template v-if="isDateRangeBelowMin">
          The campaign period must span over at least 3 days
        </template>
        <span
          v-if="!isValidDateRange"
          :data-test="`campaign-period-${periodIndex}-date-range-alert`"
        />
        <span
          v-else-if="!isValidBudget"
          :data-test="`campaign-period-${periodIndex}-budget-spend-alert`"
        >
          <template v-if="isBudgetOverMax">
            The maximum allowed budget is {{ GET_SELECTED_CURRENCY.symbolString + budgetMaxLimit }}
          </template>
          <template v-else-if="isNewBudgetBelowSpentBudget">
            The campaign has already spent more than the budget you have entered.
          </template>
        </span>
      </p>
    </b-row>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';

import CampaignPeriod from '@/modules/campaigns/components/CampaignInfo/CampaignPeriod';
import PeriodBudget from '@/modules/campaigns/components/CampaignInfo/PeriodBudget';
import ImpressionTarget from '@/modules/campaigns/components/CampaignInfo/ImpressionTarget';


import dateHelper from '@/core/mixins/dateHelper';

import {
  parseISO, isPast, isValid, differenceInDays, isWithinInterval, endOfDay, startOfDay,
} from 'date-fns';

export default {
  name: 'CampaignPeriodControls',
  mixins: [
    dateHelper,
  ],
  components: {
    CampaignPeriod,
    PeriodBudget,
    ImpressionTarget,
  },
  props: {
    periodIndex: {
      type: Number,
      required: true,
      default: 0,
    },
    period: {
      type: Object,
      required: true,
      default: () => ({
        budget: null,
        impressionTarget: null,
        startDate: null,
        endDate: null,
      }),
    },
    periods: {
      type: Array,
      required: true,
      default: () => [],
    },
    isAddImpressionTargetChecked: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      newPeriod: {
        budget: null,
        impressionTarget: null,
        startDate: null,
        endDate: null,
      },
      periodKey: 0,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
      'campaignStats',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN,
      campaignTypes.GET_SELECTED_CURRENCY,
      campaignTypes.GET_CAMPAIGN_TOTAL_BUDGET,
      campaignTypes.GET_IS_CAMPAIGN_DRAFT,
      campaignTypes.GET_IS_CAMPAIGN_REVIEW,
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
    ]),
    periodDateRange() {
      return {
        startDate: this.newPeriod.startDate,
        endDate: this.newPeriod.endDate,
      };
    },
    budgetValue() {
      return this.newPeriod.budget ?? this.period.budget;
    },
    impressionTargetValue() {
      return this.newPeriod.impressionTarget ?? this.period.impressionTarget;
    },
    budgetMaxLimit() {
      return this.GET_IS_MANAGED_CAMPAIGN ? Number.MAX_VALUE : 300000;
    },
    isBudgetOverMax() {
      return !!this.budgetValue && this.budgetValue > this.budgetMaxLimit;
    },
    ISOStartDate() {
      if (!this.newPeriod.startDate) return null;
      return parseISO(this.newPeriod.startDate);
    },
    ISOEndDate() {
      if (!this.newPeriod.endDate) return null;
      return parseISO(this.newPeriod.endDate);
    },
    isNewBudgetBelowSpentBudget() {
      if (!this.isPeriodRunning) return false;
      let periodsSum = 0;
      for (let index = 0; index < this.periods.length; index++) {
        periodsSum += this.periods[index].budget;
      }
      const budgetSpentPercentage = 100 - this.campaignStats.budgetRemaining;
      const alreadySpentBudget = (this.getOriginalCampaignTotalBudget() * budgetSpentPercentage) / 100;
      return periodsSum <= alreadySpentBudget;
    },
    isValidImpressionTarget() {
      return this.newPeriod.impressionTarget == null || this.newPeriod.impressionTarget >= 100;
    },
    isValidBudget() {
      return !!this.budgetValue
      && !this.isBudgetOverMax
      && !this.isNewBudgetBelowSpentBudget;
    },
    isDateRangeBelowMin() {
      const numberOfDays = differenceInDays(this.ISOEndDate, this.ISOStartDate) + 1;
      return numberOfDays < 3;
    },
    isValidDateRange() {
      return isValid(this.ISOStartDate)
      && isValid(this.ISOEndDate)
      && !this.isDateRangeBelowMin;
    },
    isValidPeriod() {
      return this.isValidBudget
      && this.isValidDateRange
      && this.isValidImpressionTarget;
    },
    isPeriodRunning() {
      if (this.GET_IS_CAMPAIGN_DRAFT || this.GET_IS_CAMPAIGN_REVIEW) {
        return false;
      }
      const start = startOfDay(new Date(this.newPeriod.startDate));
      const end = endOfDay(new Date(this.newPeriod.endDate));
      return isWithinInterval(new Date(), { start, end });
    },
    hasPeriodEnded() {
      if (!this.newPeriod.startDate || !this.newPeriod.endDate) return false;
      return isPast(endOfDay(new Date(this.newPeriod.endDate)));
    },
    isDeletePeriodDisabled() {
      if (this.GET_IS_CAMPAIGN_DRAFT) return false;
      return this.isPeriodRunning
      || this.hasPeriodEnded;
    },
    shouldDisplayValidationMessages() {
      return (this.budgetValue && !this.isValidBudget)
      || (this.newPeriod.startDate && this.newPeriod.endDate && !this.isValidDateRange);
    },
  },
  methods: {
    getOriginalCampaignTotalBudget() {
      return this.order.budget ?? 0;
    },
    updateBudget(budget) {
      this.newPeriod.budget = budget;
      this.emitNewPeriod();
    },
    updateImpressionTarget(impressionTarget) {
      this.newPeriod.impressionTarget = impressionTarget;
      this.emitNewPeriod();
    },
    updateDates(startDate, endDate) {
      this.newPeriod.startDate = this.convertDateToString(startDate);
      this.newPeriod.endDate = this.convertDateToString(endDate);
      this.emitNewPeriod();
    },
    emitNewPeriod() {
      this.$emit('updatePeriod', {
        budget: this.budgetValue,
        impressionTarget: this.impressionTargetValue,
        startDate: this.newPeriod.startDate,
        endDate: this.newPeriod.endDate,
      });
      this.$emit('updatePeriodsValidation', this.isValidPeriod);
    },
    deletePeriod() {
      if (this.isDeletePeriodDisabled) return;
      this.newPeriod = {
        budget: null,
        impressionTarget: null,
        startDate: null,
        endDate: null,
      };
      this.$emit('deletePeriod');
      this.periodKey++;
    },
  },
  mounted() {
    this.newPeriod.startDate = this.period.startDate;
    this.newPeriod.endDate = this.period.endDate;
  },
};
</script>
